import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import OrderKittingService from 'services/OrderKittingService';
import {
  CredentialsResponse,
  SHIPBOB_DASHBOARD_URL,
  SHIPBOB_DELAY_DOC_URL,
  StoreCredentials,
  TokenStatus,
} from '../types';

function StatusIcon({ status }: { status: TokenStatus }) {
  if (status.ok) return <CheckCircleIcon color="success" fontSize="small" />;
  if (!status.configured) return <RemoveCircleOutlineIcon color="disabled" fontSize="small" />;
  return <ErrorOutlineIcon color="error" fontSize="small" />;
}

function readLabel(status: TokenStatus): string {
  if (status.ok) return status.shopName ? `OK — ${status.shopName}` : 'OK';
  if (!status.configured) return 'Not configured';
  return 'Failing';
}

function orderEditLabel(status: TokenStatus): string {
  if (status.ok) return 'OK — all scopes granted';
  if (!status.configured) return 'Not set up';
  if (status.missingScopes?.length) return `Missing: ${status.missingScopes.join(', ')}`;
  return 'Failing';
}

function CredentialRow({ store }: { store: StoreCredentials }) {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {store.store}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {store.currency}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusIcon status={store.read} />
          <Tooltip title={store.read.error ?? ''}>
            <Typography variant="body2">{readLabel(store.read)}</Typography>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusIcon status={store.orderEdit} />
          <Tooltip title={store.orderEdit.error ?? ''}>
            <Typography variant="body2">{orderEditLabel(store.orderEdit)}</Typography>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip
            size="small"
            color={store.canKitLive ? 'success' : store.canEvaluate ? 'warning' : 'error'}
            label={
              store.canKitLive ? 'Can kit live' : store.canEvaluate ? 'Dry-run only' : 'Cannot evaluate'
            }
          />
          {store.shipbobSettingsUrl && (
            <Tooltip title={`Open ${store.store}'s ShipBob integration settings (import delay)`}>
              <IconButton
                size="small"
                component={Link}
                href={store.shipbobSettingsUrl}
                target="_blank"
                rel="noreferrer"
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

// Everything needed to take a storefront from "rules exist" to "orders actually
// get kitted": which credentials are healthy right now, how to create the
// missing ones, and where the ShipBob import delay — the thing that gives the
// job its window to act — is configured.
export default function SetupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const service = React.useMemo(() => new OrderKittingService(), []);
  const [credentials, setCredentials] = React.useState<CredentialsResponse | null>(null);
  const [error, setError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(false);
    const result = await service.getCredentials();
    if (result) setCredentials(result);
    else setError(true);
    setLoading(false);
  }, [service]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const scopes = credentials?.requiredOrderEditScopes ?? [
    'read_orders',
    'write_order_edits',
    'write_orders',
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Setup &amp; credentials</DialogTitle>
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Shopify credentials
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Checked live against Shopify each time this opens. The <strong>read</strong>{' '}
          token finds orders and fills the SKU dropdowns; the{' '}
          <strong>order-edit</strong> token is what actually performs the
          substitution. A store with no order-edit token stays in dry-run no
          matter how its switches are set.
        </Typography>

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Checking tokens…
            </Typography>
          </Stack>
        )}
        {error && (
          <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>
            Failed to check credentials.
          </Alert>
        )}
        {credentials && !loading && (
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Store</TableCell>
                  <TableCell>Read token</TableCell>
                  <TableCell>Order-edit token</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {credentials.stores.map(store => (
                  <CredentialRow key={store.store} store={store} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Creating a store&apos;s order-edit token
        </Typography>
        <Alert severity="info" sx={{ mb: 1 }}>
          Shopify stopped allowing new admin &ldquo;custom apps&rdquo; on 1 January
          2026, and the Dev Dashboard never shows a token. The token is obtained
          by authorising the app against the store once — the steps below. Apps
          are per store, so this repeats for each storefront you want to kit.
        </Alert>
        <List dense sx={{ listStyle: 'decimal', pl: 3 }}>
          {[
            <>
              In the{' '}
              <Link href="https://dev.shopify.com/dashboard" target="_blank" rel="noreferrer">
                Shopify Dev Dashboard
              </Link>
              , create an app named <code>Order Kitting</code> (no App URL of your own
              is needed — the default is fine).
            </>,
            <>
              On the app&apos;s <strong>Versions</strong> tab, set the Admin API scopes to
              exactly:{' '}
              {scopes.map(scope => (
                <Chip key={scope} label={scope} size="small" sx={{ mr: 0.5, fontFamily: 'monospace' }} />
              ))}
              add a redirect URL of <code>https://localhost:3000/auth/callback</code>, then{' '}
              <strong>Release</strong> the version. Scopes must be final before installing —
              changing them later requires re-authorising.
            </>,
            <>
              From the app&apos;s <strong>Home</strong> tab, use <strong>Install app</strong> and
              pick the store.
            </>,
            <>
              Signed in as staff on that store, open this in a browser (client id from
              the app&apos;s <strong>Settings</strong> tab):
              <Box
                component="code"
                sx={{ display: 'block', mt: 0.5, p: 1, bgcolor: '#F1F5F9', borderRadius: 1, fontSize: 11, wordBreak: 'break-all' }}
              >
                https://&lt;store&gt;.myshopify.com/admin/oauth/authorize?client_id=&lt;CLIENT_ID&gt;&amp;scope={scopes.join(',')}&amp;redirect_uri=https://localhost:3000/auth/callback&amp;state=abc123
              </Box>
              Approve it. The browser lands on a dead localhost page — that is expected;
              copy the <code>code=…</code> value out of the address bar.
            </>,
            <>
              Hand the client id, client secret and that <code>code</code> to an engineer
              promptly (the code expires in minutes). They exchange it for a permanent{' '}
              <code>shpat_…</code> token and store it in{' '}
              <code>src/shopify/config.js</code> (git-crypt encrypted) in{' '}
              <code>sbly-dash-collector</code>. This panel turns green once it deploys.
            </>,
          ].map((step, index) => (
            <ListItem key={index} sx={{ display: 'list-item', pl: 0 }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={step} />
            </ListItem>
          ))}
        </List>
        <Typography variant="caption" color="text.secondary">
          Do not send the token or secret over chat or email. Never paste them into this
          page — nothing here stores credentials.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          ShipBob import delay
        </Typography>
        <Alert severity="warning" sx={{ mb: 1 }}>
          Required before switching any store to live. Without the delay,
          ShipBob imports orders within minutes and the job has no window to
          edit them — matching orders are simply skipped as already fulfilled.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Set the order import delay to <strong>60 minutes</strong> on each store you
          kit — it is configured per storefront. ShipBob supports 30 minutes to 24
          hours; it requires Auto-Sync to be on and applies to all orders from that
          store. See{' '}
          <Link href={SHIPBOB_DELAY_DOC_URL} target="_blank" rel="noreferrer">
            ShipBob&apos;s Shopify integration documentation
          </Link>
          .
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1, gap: 1 }}>
          {(credentials?.stores ?? []).map(store =>
            store.shipbobSettingsUrl ? (
              <Button
                key={store.store}
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon fontSize="small" />}
                href={store.shipbobSettingsUrl}
                target="_blank"
                rel="noreferrer"
              >
                {store.store} import delay
              </Button>
            ) : null,
          )}
          {!credentials && (
            <Link href={SHIPBOB_DASHBOARD_URL} target="_blank" rel="noreferrer">
              ShipBob dashboard
            </Link>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Keep the delay comfortably above the maximum order age in{' '}
          <strong>Settings</strong> (capped at 55 minutes) so an order is never
          edited after ShipBob has already imported it.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Also required per store
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The kit variant must exist in that store&apos;s catalog priced at{' '}
          <strong>at least the full component value</strong> — the job only ever
          discounts it down to what the customer actually paid, so a kit priced
          too low means the order is skipped.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <strong>Sync products to ShipBob after creating the kit variant.</strong>{' '}
          ShipBob matches Shopify products on the <strong>variant ID</strong>, not
          the SKU, so a kit variant it has never synced cannot be fulfilled once
          the job puts it on an order.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ShipBob must also hold that item as a <strong>physically kitted</strong>{' '}
          product. A ShipBob &ldquo;bundle&rdquo; is virtual and still picks each unit
          separately, which saves nothing.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={load} disabled={loading}>
          Re-check
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
